import { clearRegisterSessions } from '../pos/sessions';
import { clearAllBulkDrafts } from '../pages/bulkDraft';
import { clearAllCashDrafts } from '../pages/cashDraft';
import { clearStationCaches } from '../helperStations/localStation';
import { clearIntakeCaches, purgeExpiredIntakeLists } from '../intake/localIntake';
import { clearAnalyticsCache } from '../hooks/useEventAnalytics';
import { clearAllPending } from '../lib/pendingTarget';
import { clearItemImageCache } from '../lib/itemImageCache';
import { resetPoolLabelsAvailable } from '../hooks/poolLabelsCache';
import { resetOrganizationsCache } from '../hooks/useOrganizations';
import { navContextStore } from '../components/navContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ApiClientError,
  setAccessToken,
  setSessionEndedHandler,
  refreshSession,
} from '../api/client';
import { api, type User } from '../api/endpoints';
import { onReconnect } from '../lib/connectivity';
import {
  currentPerson,
  dropTabPerson,
  forgetPerson,
  onForeignPerson,
  rememberPerson,
} from './currentPerson';
import { clearLogoutPending, isLogoutPending, markLogoutPending } from './logoutPending';
import { flushBeforeLogout } from './flushBeforeLogout';
import {
  endLocalSubscription,
  forgetSubscription,
  rememberedSubscriptionId,
} from '../push/webPush';

/** `offline`: Beim Start war kein Netz oder der Server gestört, ob eine Sitzung besteht, ist offen (Kasse, Annahme und Rückgabe arbeiten dann mit den Daten auf dem Gerät weiter). */
type Status = 'loading' | 'anonymous' | 'authenticated' | 'offline';

interface AuthValue {
  status: Status;
  user: User | null;
  login: (email: string, password: string, remember?: boolean, code?: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Nach Änderungen am Profil die Anzeige aktualisieren. */
  setUser: (user: User) => void;
}

/** So lange wartet die Oberfläche beim Start auf die Anmeldeprüfung (wie `UpdateRequired.GATE_TIMEOUT_MS`). */
export const START_TIMEOUT_MS = 4000;

type StartOutcome =
  | { kind: 'ok'; user: User }
  | { kind: 'none' }
  /** Server nicht erreichbar oder nicht gesund: ob eine Sitzung besteht, ist offen. */
  | { kind: 'offline' };

/** Kein Netz, Zeitlimit (beides `NETWORK`), Serverfehler oder Überlast: kein Grund, jemanden abzumelden. */
function isServerTrouble(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    (error.code === 'NETWORK' || error.status >= 500 || error.status === 429)
  );
}

/** Nur eine Antwort „Cookie ist schon ungültig“ (401) oder ein Erfolg erledigt den Server-Logout; andere Absagen (4xx) lassen das Merkzeichen stehen. */
const settlesLogout = (error: unknown): boolean =>
  error instanceof ApiClientError && error.status === 401;

/** Der eine laufende Nachholversuch: Anmeldung und „Netz zurück“ warten auf denselben (kein doppelter Logout, kein Wettlauf um das Merkzeichen). */
let settling: Promise<void> | null = null;

/**
 * Holt einen ausstehenden Server-Logout nach (Abmeldung ohne Netz). Gelingt er oder ist er nicht mehr nötig, wird das Merkzeichen
 * gelöscht; bei Netz- oder Serverproblemen und bei anderen Absagen bleibt es für den nächsten Versuch. Es läuft höchstens ein
 * Versuch zugleich. Wirft nie.
 */
function settleLogout(): Promise<void> {
  if (!isLogoutPending()) return Promise.resolve();
  settling ??= (async () => {
    try {
      await api.logout(rememberedSubscriptionId() ?? undefined);
      clearLogoutPending();
      // Der Server hat das Abo dieses Geräts für Mitteilungen gelöscht: lokal ebenso
      await forgetSubscription();
    } catch (error) {
      if (settlesLogout(error)) {
        clearLogoutPending();
        await forgetSubscription();
      }
    } finally {
      settling = null;
    }
  })();
  return settling;
}

async function checkSession(): Promise<StartOutcome> {
  // Wer sich abgemeldet hat, bleibt abgemeldet: kein Erneuern über das (noch gültige) Cookie, erst der Server-Logout
  if (isLogoutPending()) {
    await settleLogout();
    return { kind: 'none' };
  }
  try {
    if (!(await refreshSession())) return { kind: 'none' };
    return { kind: 'ok', user: await api.me() };
  } catch (error) {
    return isServerTrouble(error) ? { kind: 'offline' } : { kind: 'none' };
  }
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<User | null>(null);

  // Beim Start eine vorhandene Sitzung (Refresh-Cookie) wiederherstellen, aber höchstens 4 s lang. Ohne Netz, bei hängendem oder
  // kaputtem Server (Zeitlimit, 5xx, 429) bleibt der Zustand „offline“: Die Kasse darf ohne Anmeldung weiterarbeiten (ihre Daten
  // liegen schon auf dem Gerät). Nur ein echtes 401 heißt „abgemeldet“. Sobald das Netz zurück ist, wird geprüft.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const guard = { cancelled: false };
    const isCancelled = (): boolean => guard.cancelled;
    const apply = (outcome: StartOutcome): void => {
      if (isCancelled()) return;
      if (outcome.kind === 'ok') {
        rememberPerson(outcome.user.id);
        setUser(outcome.user);
        setStatus('authenticated');
      } else if (outcome.kind === 'offline') {
        setStatus('offline');
      } else {
        forgetPerson();
        // Abgelaufene Sitzung beim Start: Körbe und Verkäuferliste (Namen) gehören niemandem mehr. Wartende Scans bleiben
        // (beide Löschläufe räumen nur Zwischenspeicher, nie die Warteschlange).
        void clearStationCaches();
        void clearIntakeCaches();
        setStatus('anonymous');
      }
    };
    const work = checkSession();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<StartOutcome>((resolve) => {
      timer = setTimeout(() => {
        resolve({ kind: 'offline' });
      }, START_TIMEOUT_MS);
    });
    void (async () => {
      const first = await Promise.race([work, timeout]);
      clearTimeout(timer);
      apply(first);
      // Kam die Antwort erst nach dem Zeitlimit, gilt sie jetzt: eine gültige Sitzung wird übernommen, eine echte Absage (401)
      // heißt abgemeldet. Sonst bliebe die Oberfläche bei „Kein Netz“, obwohl der Server antwortet, und eine abgelaufene
      // Anmeldung hielte Kasse und Station ohne gültige Sitzung offen. Das Zeitlimit der Erneuerung (8 s) begrenzt das Fenster.
      if (first.kind === 'offline') {
        const late = await work;
        if (late.kind !== 'offline') apply(late);
      }
    })();
    return () => {
      guard.cancelled = true;
      clearTimeout(timer);
    };
  }, [attempt]);

  // Netz wieder da (oder die App kommt in den Vordergrund): die Sitzung jetzt klären
  useEffect(() => {
    if (status !== 'offline') return;
    return onReconnect(() => {
      setAttempt((n) => n + 1);
    });
  }, [status]);

  // Netz zurück: ein ausstehender Server-Logout wird nachgeholt, auch wenn niemand angemeldet ist
  useEffect(() => onReconnect(() => void settleLogout()), []);

  // Beim Start: abgelaufene Verkäuferlisten der Annahme löschen (auch von Basaren, die nicht mehr geöffnet werden)
  useEffect(() => {
    void purgeExpiredIntakeLists();
  }, []);

  useEffect(() => {
    setSessionEndedHandler(() => {
      forgetPerson();
      clearAllBulkDrafts();
      clearAllCashDrafts();
      void clearStationCaches();
      void clearIntakeCaches();
      clearAnalyticsCache();
      clearItemImageCache();
      resetOrganizationsCache();
      resetPoolLabelsAvailable();
      navContextStore.reset();
      setUser(null);
      setStatus('anonymous');
    });
  }, []);

  // Ein anderer Tab hat eine andere Person angemeldet: Dieser Tab beendet seine Sitzung nur lokal (kein Server-Logout, kein
  // Löschen gemeinsamer Daten: Beides gehört jetzt der Person im anderen Tab) und sendet nie mehr unter dem alten Token.
  useEffect(
    () =>
      onForeignPerson(() => {
        setAccessToken(null);
        dropTabPerson();
        clearAnalyticsCache();
        resetOrganizationsCache();
        resetPoolLabelsAvailable();
        navContextStore.reset();
        setUser(null);
        setStatus('anonymous');
      }),
    [],
  );

  const login = useCallback(
    async (email: string, password: string, remember = true, code?: string) => {
      // Ein ausstehender Server-Logout zuerst: Die neue Anmeldung ersetzt das Cookie, das alte bliebe sonst für immer gültig
      await settleLogout();
      const result =
        code === undefined
          ? await api.login(email, password, remember)
          : await api.login(email, password, remember, code);
      // Wer sich anmeldet, sieht nie zwischengespeicherte Auswertungen einer früheren Sitzung
      clearAnalyticsCache();
      resetOrganizationsCache();
      resetPoolLabelsAvailable();
      navContextStore.reset();
      clearLogoutPending();
      // Meldet sich eine andere Person an als die zuletzt bekannte, gehört ihr die gespeicherte Verkäuferliste der früheren nicht
      if (currentPerson() !== result.user.id) void clearIntakeCaches();
      setAccessToken(result.accessToken);
      rememberPerson(result.user.id);
      setUser(result.user);
      setStatus('authenticated');
    },
    [],
  );

  const logout = useCallback(async () => {
    // Wartende Rückgaben und Verkäufe dieser Person gehen noch unter ihrem Konto hinaus (mit Netz, nur kurz): danach ist es zu
    // spät, sie würden sonst bis zur nächsten Anmeldung dieser Person warten. Wirft nie.
    await flushBeforeLogout();
    // Ab hier meldet nichts mehr still über das Cookie an. Das Zeichen bleibt, bis der Server-Logout gelungen ist: Ohne Netz
    // ist die Person lokal sofort abgemeldet, das Cookie wird beim nächsten Netz widerrufen (siehe `settleLogout`).
    markLogoutPending();
    let failure: Error | null = null;
    try {
      // Mit der Kennung des Abos dieses Geräts für Mitteilungen: Wer sich abmeldet, bekommt dort nichts mehr
      await api.logout(rememberedSubscriptionId() ?? undefined);
      clearLogoutPending();
      await forgetSubscription();
    } catch (error) {
      if (!isServerTrouble(error)) {
        if (settlesLogout(error)) {
          clearLogoutPending();
          await forgetSubscription();
        }
        failure = error instanceof Error ? error : new Error('Abmelden fehlgeschlagen.');
      }
    }
    // Das Abo im Browser endet in jedem Fall mit der Abmeldung (auch ohne Netz): Die Kennung bleibt für den Server-Logout
    // gemerkt, aber der Push-Dienst nimmt nichts mehr für die nächste Person an dieses Gerät an
    await endLocalSubscription();
    // Die Kassen-Sitzung bleibt nicht für die nächste Person am Gerät (Kasse und Warteschlange bleiben erhalten)
    await clearRegisterSessions();
    // Entwürfe der Schnellerfassung bleiben nicht für die nächste Person am Gerät
    clearAllBulkDrafts();
    clearAllCashDrafts();
    // Körbe der Rückgabe-Station (offline ohne Anmeldung erreichbar) bleiben nicht für die nächste Person
    await clearStationCaches();
    // Die gespeicherte Verkäuferliste der Annahme (mit Namen) bleibt nicht für die nächste Person; wartende Scans bleiben
    await clearIntakeCaches();
    // Auswertungen (Zwischenspeicher im Modul) gehören nicht der nächsten Person am Gerät
    clearAnalyticsCache();
    // Artikelbilder im Arbeitsspeicher (Welle 4g) gehören nicht der nächsten Person am Gerät
    clearItemImageCache();
    // Gemerktes Ziel, Helfer-Link und Einladung (F5) gehören nicht der nächsten Person am Gerät
    clearAllPending();
    // Organisationen und zuletzt geöffneter Basar gehören nicht der nächsten Person am Gerät
    resetOrganizationsCache();
    // Ob Dauer-Etiketten verfügbar sind, gilt je Sitzung
    resetPoolLabelsAvailable();
    navContextStore.reset();
    setAccessToken(null);
    forgetPerson();
    setUser(null);
    setStatus('anonymous');
    if (failure !== null) throw failure;
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, user, login, logout, setUser }),
    [status, user, login, logout],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) throw new Error('useAuth braucht den AuthProvider.');
  return value;
}
